import React, { useRef, useEffect, Suspense, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./redux/action/auth_action";
import Loader from "./components/Common/loader";
import AuthLayout from "./components/Layout/AuthLayout";
import MainLayout from "./components/Layout/MainLayout";
import Receipt from "pages/Estimate/Receipt";
import PatientReceipt from "pages/PatientAppointmentDetail/PatientReceipt";
import TRFWorkSheet from "pages/PatientAppointmentDetail/TRFWorkSheet";
import { useAlert } from "context/alert";
import {
  generateToken,
  messaging,
  onMessageListener,
} from "notifications/firebase";
import { onMessage } from "firebase/messaging";
import ResultEntryPdf from "./pages/Report/PatientLabSearchReport/resultEntryPdf";
import SampleReportPdf from "./pages/InvestigationManager/SampleReportPdf";
import toast, { Toaster } from "react-hot-toast";
import PatientDocument from "pages/Report/PatientLabSearchReport/PatientDocument";
import axiosInstance from "services/axios";
import { ROLE_BASED_ROUTES } from "roleBasedRoutes";
import Dashboard from "pages/Dashboard";
import PatientReceiptCreditMode from "pages/PatientAppointmentDetail/PatientReceiptCreditMode";
import ReceiptB2C from "pages/Estimate/ReceiptB2C";
import Receipt_B2C from "pages/Estimate/Receipt_B2C";
import NewReportPdf from "pages/Report/PatientLabSearchReport/newReportPdf";

const Login = React.lazy(() => import("./pages/login"));
const Invoice = React.lazy(() => import("./pages/NewRegistration/invoice"));
const NewRegistrationPatientReceipt = React.lazy(
  () => import("./pages/NewRegistration/NewRegistrationPatientReceipt"),
);
const ResultEntryHtmlPdf = React.lazy(
  () => import("./pages/Report/PatientLabSearchReport/resultEntryHtmlPdf"),
);
const ResultEntryReactPdf = React.lazy(
  () => import("./pages/Report/PatientLabSearchReport/resultEntryPdfReact"),
);
const ResultEntryPdfDownloadQRCode = React.lazy(
  () =>
    import("./pages/Report/PatientLabSearchReport/ResultEntryPdfDownloadQRCode"),
);
const PaymentSuccess = React.lazy(
  () => import("./components/Common/Success/PaymentSuccess"),
);
const FailurePayment = React.lazy(
  () => import("./components/Common/Failure/FailurePayment"),
);

const ProtectedRoute = ({
  children,
  allowedRoles,
  routeLabel,
  allowAll = false,
}) => {
  const { user } = useSelector((state) => state.jwtAuthreducer);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowAll) {
    return children;
  }

  const isSuperAdmin = user?.employee_roles?.some(
    (role) => role.role_name === "SUPER ADMIN",
  );
  if (isSuperAdmin) return children;

  const isFranchiseEmployee =
    Number(localStorage.getItem("is_franchise_employee")) === 1;
  const isSuperFranchise =
    Number(localStorage.getItem("is_super_franchise")) === 1;
  const isSubFranchise =
    Number(
      localStorage.getItem("is_sub_franchise") ??
        localStorage.getItem("sub_franchise"),
    ) === 1;

  const userRolesNames = user?.employee_roles?.map((r) => r.role_name) || [];

  const allUserRoles = [...userRolesNames];
  if (isSuperFranchise) allUserRoles.push("SUPER_FRANCHISE");
  if (isFranchiseEmployee) allUserRoles.push("FRANCHISE_EMPLOYEE");
  if (isSubFranchise) allUserRoles.push("SUB_FRANCHISE");

  if (allowedRoles && allowedRoles.length > 0) {
    const hasRoleAccess = allowedRoles.some((role) =>
      allUserRoles.includes(role),
    );
    if (hasRoleAccess) {
      return children;
    }

    if (isSuperFranchise || isFranchiseEmployee || isSubFranchise) {
      return <Navigate to="/" replace />;
    }
  }

  const userRoleIds = user?.employee_roles?.map((r) => r.role_id) || [];
  const allowedSections = user?.allowed_section || [];

  const normalizeText = (text) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .trim()
      .replace(/[\s-_]+/g, "");
  };

  if (routeLabel) {
    const normalizedRouteLabel = normalizeText(routeLabel);

    const isSectionAllowed = allowedSections.some((section) => {
      const isNameMatch =
        normalizeText(section.section) === normalizedRouteLabel;
      const isRoleIdMatch = userRoleIds.includes(section.role_id);
      return isNameMatch && isRoleIdMatch;
    });

    if (isSectionAllowed) {
      return children;
    }
  }

  return <Navigate to="/" replace />;
};

function App() {
  const isMounted = useRef(false);
  const dispatch = useDispatch();
  const login = useSelector((state) => state.jwtAuthreducer);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    if (isMounted.current) return;
    dispatch(AuthProvider());
    isMounted.current = true;
  }, []);

  useEffect(() => {
    if (login?.user) return;

    const fetchUser = async () => {
      try {
        const token = new URLSearchParams(window.location.search).get("token");
        if (!token) return;

        const response = await axiosInstance.get("/hrms/me", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUserData(response?.data);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };

    fetchUser();
  }, [login?.user]);

  useEffect(() => {
    generateToken();
    onMessageListener();
    onMessage(messaging, (payload) => {
      toast((t) => (
        <div>
          <strong>{payload.notification.title}</strong>
          <p>{payload.notification.body}</p>
        </div>
      ));
    });
  }, []);

  if (login.loading) {
    return <Loader />;
  }

  return (
    <div className="App">
      <Toaster position="top-right" />
      <Router>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/receipt" element={<Receipt />} />
            <Route path="/receipt-b2c" element={<Receipt_B2C />} />
            <Route path="/receipt-cash-mode" element={<ReceiptB2C />} />
            <Route path="/receipt-patient" element={<PatientReceipt />} />
            <Route
              path="/receipt-patient-credit-mode"
              element={<PatientReceiptCreditMode />}
            />
            <Route path="/receipt-trf-work" element={<TRFWorkSheet />} />
            <Route path="/invoice" element={<Invoice />} />
            <Route
              path="/invoice-new-registration-patient-receipt"
              element={<NewRegistrationPatientReceipt />}
            />
            <Route
              path="/ResultEntryPdf"
              element={
                // hasPdfUIChangeRole ?
                <ResultEntryPdf />
                // : <ResultEntryPdfCopy />
              }
            />
            <Route path="/NewReportPdf" element={<NewReportPdf />} />
            <Route path="/SampleReportPdf" element={<SampleReportPdf />} />
            <Route path="/patient-document" element={<PatientDocument />} />
            <Route
              path="/ResultEntryHtmlPdf"
              element={<ResultEntryHtmlPdf />}
            />
            <Route
              path="/ResultEntryReactPdf"
              element={<ResultEntryReactPdf />}
            />
            <Route
              path="/medicity/reports/:token"
              element={<ResultEntryPdfDownloadQRCode />}
            />
            <Route
              path="/appointment-payment/advancepayment-success"
              element={<PaymentSuccess />}
            />
            <Route
              path="/appointment-payment/advancepayment-failure"
              element={<FailurePayment />}
            />

            <Route element={<AuthLayout />}>
              <Route path="/" element={<MainLayout />}>
                <Route
                  index
                  element={
                    <ProtectedRoute allowAll={true}>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />

                {ROLE_BASED_ROUTES.filter((route) => route.path !== "/").map(
                  (route, index) => (
                    <Route
                      key={index}
                      path={route.path}
                      element={
                        <ProtectedRoute
                          allowedRoles={route.roles}
                          routeLabel={route.label}
                        >
                          {route.element}
                        </ProtectedRoute>
                      }
                    />
                  ),
                )}
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </div>
  );
}

export default App;
